// frontend/src/pages/AboutPage.js

import React from "react";
import PreLoginHeader from "../components/PreLoginHeader";

function AboutPage({ embedded = false }) {
  return (
    <div
      className={
        embedded
          ? "bg-blue-50 text-[#181411]"
          : "min-h-screen bg-blue-50 text-[#181411]"
      }
    >
      {!embedded && <PreLoginHeader />}

      {/* Hero */}
      {!embedded && (
        <section className="px-6 pt-[4.45rem] text-center md:px-10 lg:pb-6 lg:pt-24">
          <h1 className="mx-auto max-w-3xl text-2xl font-black leading-[0.95] tracking-[-0.03em] text-gray-900 lg:text-[38px]">
            "
            <span className="whitespace-nowrap text-blue-500">
              GOOOOOD JOB!
            </span>
            "
          </h1>
        </section>
      )}

      {/* Manifesto */}
      <main className="mx-auto max-w-4xl px-4 pb-4 md:px-10">
        <article className="rounded-3xl bg-transparent px-5 sm:px-6 sm:px-8 pt-3 sm:pt-0 sm:pb-6">
          <div className="space-y-4 sm:space-y-5 text-[16px] leading-normal text-gray-700 sm:text-[18px]">
            <p>
              My husband runs every day. He runs to decompress after work, to
              clear his head, and to keep a promise to himself. More often than
              not, he&apos;s squeezing in miles on a treadmill after a long
              day. His job is competitive enough already, so running isn&apos;t
              another arena to compete – it&apos;s where he rebuilds his energy.
              He&apos;d run whether anyone saw it or not.
            </p>

            <p>
              And yet, every single day, he sends me a screenshot of his Garmin
              run. And every single day, I send back the same three emojis – a
              high five hand, a heart, a sparkle – and "GOOOOD JOB!" in all caps,
              extra O&apos;s included. He sends his own combo back. We&apos;ve
              been doing this for years, and he still says it gives him this
              specific feeling of warmth, like he&apos;s seen, like he&apos;s
              loved.
            </p>

            <p>
              I do the same for him: a photo of the last page of a book I just
              finished, a shaky video of me nervously releasing the steam from
              an Instant Pot for the second time, a voice memo of remixing a
              Charli XCX song in the free version of a DJ app, or sometimes just
              a text saying I finally did three minutes of breathing exercises
              on an anxious day. None of it is impressive. All of it gets a
              virtual high five.
            </p>

            <p className="font-semibold text-gray-900">
              I&apos;ve come to think that this ritual says more about what I
              believe than almost anything else I could tell you.
            </p>

            <p>
              When we were kids, there were little rituals of encouragement
              everywhere – a gold star on a math test, a new belt in taekwondo,
              a drawing hung on the refrigerator. They weren&apos;t about
              pretending every effort was extraordinary. They were reminders
              that showing up, trying, and growing were worth noticing.
            </p>

            <p>
              As we got older, celebration got rationed. Gold stars were reserved
              for the milestones, and encouragement was traded for expectations –
              especially for the parts of life beyond home and work that make it
              feel full. The spaces where we can simply be: explore, fumble,
              connect. Sociologists have a name for them: third places.
            </p>

            <p>
              We never stopped showing up there. We just stopped getting gold
              stars and high fives for it. Somewhere along the way, we stopped
              giving each other, and ourselves, as much permission to celebrate
              those in-between moments for what they are: not just steps on the
              way to something bigger, but life itself.
            </p>

            <p>
              I&apos;ll never forget running my first marathon at 22. As I came
              down the final stretch through Central Park, I realized the
              memories that stayed with me went far beyond the medal: my friend
              Rose waking up at{" "}
              <span className="whitespace-nowrap">4 a.m.</span>{" "}
              to French braid my hair before the race, sharing running routes
              with the students in my NYU dorm where I was an RA, and running
              through Seoul with friends I never would have met otherwise.
            </p>

            <p>
              Everything that happened on the way there is what made my medal
              mine – the early morning runs through grueling summer humidity
              before class, the post-run beers with teammates who had once been
              strangers, and the ridiculous satisfaction of clearing level after
              level as I counted down the miles to Volt on Nike Run Club. All the
              ups and downs that slowly convinced me I was capable of more than I
              thought.
            </p>

            <p>
              And this is why I think we&apos;ve built our culture backwards.
            </p>

            <blockquote className="my-7 border-l-4 border-blue-500 pl-5 text-[18px] font-black leading-snug tracking-[-0.025em] text-gray-900 sm:text-[22px]">
              We&apos;ve built entire platforms to celebrate the finish line,
              and almost nothing to celebrate the journey.
            </blockquote>

            <p>
              Staying true to ourselves – to try new things, to be imperfect at
              them, and to keep going – has only gotten harder in a world
              increasingly filled with curated social media and AI-generated
              content.
            </p>

            <p>
              <span className="font-semibold text-gray-900">
                Third Place is my answer to that: a place that celebrates
                becoming, not just arriving.
              </span>{" "}
              Holding a plank for ten seconds, then
              fifteen. Volunteering on a Saturday, week after week. Meditating
              on a day your mind won&apos;t slow down. Because the moments that
              shape our lives deserve to be seen. Because sometimes the simplest
              way to tell someone "I&apos;m proud of you" is to notice that they
              showed up. And I believe those moments are what a meaningful life
              is actually made of.
            </p>

            <div className="text-[18px] sm:text-[22px] font-black leading-snug tracking-[-0.02em] text-blue-600">
              <p>So: show up.</p>
              <p>Share the ordinary and the extraordinary.</p>
              <p>Give the high five.</p>
              <p>Let it be small. Let it be big.</p>
              <p>Let it be yours.</p>
            </div>

            <div className="">
              <p className="text-[16px] sm:text-[18px] leading-snug font-medium text-gray-700">
                See you around,
              </p>

              <p className="text-[16px] sm:text-[18px] font-black leading-relaxed sm:leadingtracking-[-0.02em] text-blue-600">
                Jenn
              </p>
              <p className="text-[18px] mt-0.5 sm:text-xl"> 🫸 💙 ✨  </p>
            </div>
          </div>
        </article>
      </main>
    </div>
  );
}

export default AboutPage;
